/**
 * Utilitaires pour le format de concordance (2, 3, 5 ou 7 parties ; Lot|CodeDistrict|CadastreCode|RankCode|PropertyDistrict|Cadastre|RankLabel pour le complet).
 * U3141 – Concordance format complet et rangs (MA-87).
 * Directive backend : même lot sur plusieurs rangs → une entrée par rang au format complet ; Create/Update envoie le tableau concordance tel quel.
 */

export interface U3141ParsedConcordanceEntry {
  lot: string;
  codeDistrict?: string;
  cadastreCode?: string;
  rankCode?: string;
  propertyDistrict?: string;
  cadastre?: string;
  rankLabel?: string;
}

/** Contexte du lot parent pour l'affichage conditionnel (même district/cadastre = afficher seulement lot + rang). */
export interface U3141ConcordanceParentContext {
  codeDistrict?: string;
  cadastreCode?: string;
}

const SEPARATOR = "|";

/**
 * Retire le suffixe partie de lot `(ptie)` du segment lot (aligné sur le backend `rfLotDetailConcordanceDisplay`).
 */
export function stripPartieSuffixFromLotSegment(lotSegment: string): string {
  const s = (lotSegment ?? "").trim();
  return s.replace(/\s*\(ptie\)\s*$/i, "").trim();
}

/** Indique si le segment lot porte le suffixe partie de lot `(ptie)`. */
export function lotPartieDeLotFromLotSegment(lotSegment: string): boolean {
  return /\s*\(ptie\)\s*$/i.test((lotSegment ?? "").trim());
}

/** Formate le segment lot pour une entrée concordance (ajoute `(ptie)` si partie de lot). */
export function formatLotSegmentForConcordance(lot: string, partieDeLot?: boolean | null): string {
  const base = stripPartieSuffixFromLotSegment(lot);
  if (!base) return "";
  return partieDeLot === true ? `${base}(ptie)` : base;
}

/**
 * Parse une entrée de concordance (format simple, 2 parties, 3 parties, 5 parties ou 7 parties).
 * - Sans `|` : lot seul.
 * - 2 parties (lot|rankLabel) : lot + rankLabel.
 * - 3 parties (lot|propertyDistrict|rankLabel) : ex. affichage fusion PDF / segments alignés sur le libellé concordance.
 * - 5 parties (rétrocompat) : lot, codeDistrict, cadastreCode, rankCode, rankLabel.
 * - 7 parties : lot, codeDistrict, cadastreCode, rankCode, propertyDistrict, cadastre, rankLabel.
 */
export function parseConcordanceEntry(entry: string): U3141ParsedConcordanceEntry {
  const trimmed = (entry ?? "").trim();
  if (!trimmed) {
    return { lot: "" };
  }
  if (!trimmed.includes(SEPARATOR)) {
    return { lot: trimmed };
  }
  const parts = trimmed.split(SEPARATOR).map((p) => p.trim());
  if (parts.length >= 7) {
    return {
      lot: parts[0] ?? "",
      codeDistrict: parts[1] || undefined,
      cadastreCode: parts[2] || undefined,
      rankCode: parts[3] || undefined,
      propertyDistrict: parts[4] || undefined,
      cadastre: parts[5] || undefined,
      rankLabel: parts[6] || undefined,
    };
  }
  if (parts.length >= 5) {
    return {
      lot: parts[0] ?? "",
      codeDistrict: parts[1] || undefined,
      cadastreCode: parts[2] || undefined,
      rankCode: parts[3] || undefined,
      rankLabel: parts[4] || undefined,
    };
  }
  if (parts.length === 2) {
    return {
      lot: parts[0] ?? "",
      rankLabel: parts[1] || undefined,
    };
  }
  if (parts.length === 3) {
    return {
      lot: parts[0] ?? "",
      propertyDistrict: parts[1] || undefined,
      rankLabel: parts[2] || undefined,
    };
  }
  return { lot: parts[0] ?? "" };
}

/**
 * Construit le libellé d'affichage pour une entrée.
 * Si district et cadastre de l'entrée sont identiques au lot parent : afficher uniquement lot et rankLabel (si non vide).
 * Sinon : afficher Lot – Cadastre – Rang (on n'affiche pas le district, car c'est le même que celui du lot).
 */
export function formatConcordanceEntryForDisplay(
  entry: string,
  parentContext?: U3141ConcordanceParentContext
): string {
  const parsed = parseConcordanceEntry(entry);
  if (!parsed.lot) return "";

  const parentCodeDistrict = parentContext?.codeDistrict ?? "";
  const parentCadastreCode = parentContext?.cadastreCode ?? "";
  const sameDistrict =
    (parsed.codeDistrict ?? "") === parentCodeDistrict || (!parsed.codeDistrict && !parentCodeDistrict);
  const sameCadastre =
    (parsed.cadastreCode ?? "") === parentCadastreCode || (!parsed.cadastreCode && !parentCadastreCode);

  if (sameDistrict && sameCadastre) {
    const rankLabel = (parsed.rankLabel ?? "").trim();
    return rankLabel ? `${parsed.lot} – ${rankLabel}` : parsed.lot;
  }

  const parts: string[] = [parsed.lot];
  if (parsed.cadastre) parts.push(parsed.cadastre);
  else if (parsed.cadastreCode) parts.push(parsed.cadastreCode);
  else if (parsed.propertyDistrict) parts.push(parsed.propertyDistrict);
  if (parsed.rankLabel) parts.push(parsed.rankLabel);
  return parts.join(" – ");
}

/**
 * Construit une string de concordance au format complet (7 parties).
 * Lot|CodeDistrict|CadastreCode|RankCode|PropertyDistrict|Cadastre|RankLabel
 */
export function buildFullConcordanceEntry(
  lot: string,
  codeDistrict: string,
  cadastreCode: string,
  rankCode: string,
  propertyDistrict: string,
  cadastre: string,
  rankLabel: string
): string {
  return [lot, codeDistrict, cadastreCode, rankCode, propertyDistrict, cadastre, rankLabel].join(SEPARATOR);
}

/** Objet district minimal pour la reconstruction des strings de concordance. */
export interface U3141ConcordanceRowDistrictRef {
  codeDistrict?: string;
  propertyDistrict?: string;
}

/** Objet cadastre minimal pour la reconstruction des strings de concordance. */
export interface U3141ConcordanceRowCadastreRef {
  cadastreCode?: string;
  rankCode?: string;
  rankLabel?: string;
  cadastre?: string;
}

/** Modèle d'une ligne du popup concordance (dialogue + utilitaire de save). */
export interface U3141ConcordanceRowModel {
  lot: string;
  /** Partie de lot — encodée `(ptie)` sur le segment lot à la sauvegarde. */
  partieDeLot?: boolean;
  /** String brute reçue à l'ouverture du popup — préservée si la ligne n'est pas modifiée. */
  sourceEntry?: string;
  /** True si l'utilisateur a modifié la ligne (lot, district ou cadastre). */
  dirty?: boolean;
  codeDistrict?: string;
  cadastreCode?: string;
  rankCode?: string;
  rankLabel?: string;
  /** Libellé district parsé (format 7 ou 3 parties). */
  propertyDistrict?: string;
  /** Libellé cadastre parsé (format 7 parties). */
  cadastreLabel?: string;
  district?: U3141ConcordanceRowDistrictRef;
  cadastre?: U3141ConcordanceRowCadastreRef;
}

/** Construit une row éditable à partir d'une entrée concordance brute. */
export function rowFromConcordanceEntry(entry: string, partieDeLotFromApi?: boolean): U3141ConcordanceRowModel {
  const trimmedEntry = (entry ?? "").trim();
  const parsed = parseConcordanceEntry(trimmedEntry);
  const partieDeLot = partieDeLotFromApi === true || lotPartieDeLotFromLotSegment(parsed.lot);
  return {
    lot: stripPartieSuffixFromLotSegment(parsed.lot),
    partieDeLot,
    sourceEntry: trimmedEntry || undefined,
    dirty: false,
    codeDistrict: parsed.codeDistrict,
    cadastreCode: parsed.cadastreCode,
    rankCode: parsed.rankCode,
    rankLabel: parsed.rankLabel,
    propertyDistrict: parsed.propertyDistrict,
    cadastreLabel: parsed.cadastre,
  };
}

export interface U3141BuildConcordanceStringsResult {
  strings: string[];
  concordancePartieDeLot: boolean[];
  errors: string[];
}

/**
 * Tente de produire une string concordance au format 7 parties à partir des codes/objets de la ligne.
 * rankLabel peut être vide (ex. rang « A » sans libellé affiché).
 */
export function tryBuildFullConcordanceStringFromRow(
  row: U3141ConcordanceRowModel,
  parentContext?: U3141ConcordanceParentContext
): string | null {
  const lotBase = stripPartieSuffixFromLotSegment(row.lot ?? "");
  if (!lotBase) return null;
  const lot = formatLotSegmentForConcordance(lotBase, row.partieDeLot === true);

  const codeDistrict = row.codeDistrict ?? row.district?.codeDistrict ?? parentContext?.codeDistrict ?? "";
  const cadastreCode = row.cadastreCode ?? row.cadastre?.cadastreCode ?? parentContext?.cadastreCode ?? "";
  const rankCode = row.rankCode ?? row.cadastre?.rankCode ?? "";
  const rankLabel = row.rankLabel ?? row.cadastre?.rankLabel ?? "";
  const propertyDistrict = row.district?.propertyDistrict ?? row.propertyDistrict ?? "";
  const cadastre = row.cadastre?.cadastre ?? row.cadastreLabel ?? "";

  if (codeDistrict && cadastreCode && rankCode) {
    return buildFullConcordanceEntry(lot, codeDistrict, cadastreCode, rankCode, propertyDistrict, cadastre, rankLabel);
  }
  return null;
}

/**
 * Construit le tableau concordance[] à partir des lignes du popup.
 * Priorité : format 7 parties complet pour chaque ligne (codes parsés, autocomplete ou parentContext),
 * puis sourceEntry si la ligne n'est pas modifiée, sinon erreur explicite (pas de lot seul silencieux).
 */
export function buildConcordanceStringsFromRows(
  rows: U3141ConcordanceRowModel[],
  parentContext?: U3141ConcordanceParentContext
): U3141BuildConcordanceStringsResult {
  const errors: string[] = [];
  const strings: string[] = [];
  const concordancePartieDeLot: boolean[] = [];

  rows.forEach((row, index) => {
    const lotBase = stripPartieSuffixFromLotSegment(row.lot ?? "");
    if (!lotBase) return;

    const pushRow = (entry: string, partieDeLot: boolean) => {
      strings.push(entry);
      concordancePartieDeLot.push(partieDeLot);
    };

    const full = tryBuildFullConcordanceStringFromRow(row, parentContext);
    if (full) {
      pushRow(full, row.partieDeLot === true);
      return;
    }

    if (!row.dirty && row.sourceEntry?.trim()) {
      pushRow(row.sourceEntry.trim(), row.partieDeLot === true);
      return;
    }

    errors.push(`Ligne ${index + 1} (lot « ${lotBase} ») : district et cadastre requis pour enregistrer.`);
  });

  return { strings, concordancePartieDeLot, errors };
}

export interface U3141ValidateConcordanceResult {
  valid: boolean;
  errors?: string[];
}

/**
 * Valide le tableau d'entrées de concordance :
 * - Les strings contenant `|` doivent avoir 2, 5 ou 7 parties.
 * - Pas de doublons (même string deux fois).
 */
export function validateConcordanceEntries(entries: string[]): U3141ValidateConcordanceResult {
  const errors: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < (entries ?? []).length; i++) {
    const entry = (entries[i] ?? "").trim();
    if (!entry) continue;

    if (entry.includes(SEPARATOR)) {
      const parts = entry.split(SEPARATOR);
      const validLengths = [2, 3, 5, 7];
      if (!validLengths.includes(parts.length)) {
        errors.push(`Entrée "${entry}" : format invalide (attendu 2, 3, 5 ou 7 parties séparées par |).`);
      }
    }

    if (seen.has(entry)) {
      errors.push(`Doublon : "${entry}".`);
    }
    seen.add(entry);
  }

  return {
    valid: errors.length === 0,
    ...(errors.length > 0 && { errors }),
  };
}

/** Élément avec lot et concordanceOrigines (ex. U3141ChildLotChain). */
export interface U3141SortableByConcordance {
  id: string;
  lotDetail?: { lot?: string; concordanceOrigines?: string[] };
}

/**
 * Normalise le numéro de lot pour le matching (espaces retirés, suffixe `(ptie)` retiré — MA-135).
 */
function normalizeLotForSort(lot: string): string {
  const collapsed = (lot ?? "").replace(/\s+/g, "").trim();
  return stripPartieSuffixFromLotSegment(collapsed);
}

/**
 * Trie un tableau pour que le parent (référencé dans concordanceOrigines) apparaisse avant l'enfant.
 * Ex. lots 23, 23-9 (origine 23), 23-9-1 (origine 23-9) → ordre 23, 23-9, 23-9-1.
 * Utilise un tri topologique ; en cas de cycle ou d'erreur, retourne le tableau inchangé.
 */
export function sortByConcordanceOrder<T extends U3141SortableByConcordance>(rows: T[]): T[] {
  if (!Array.isArray(rows) || rows.length <= 1) return rows;

  const n = rows.length;
  const indexById = new Map<string, number>();
  rows.forEach((r, i) => indexById.set(String(r.id), i));

  // childrenOf[i] = indices des lignes qui doivent venir APRÈS i (i est leur parent)
  const childrenOf: number[][] = rows.map(() => []);
  for (let i = 0; i < n; i++) {
    const row = rows[i];
    const origins = row.lotDetail?.concordanceOrigines ?? [];
    const firstOrigin = origins[0];
    if (!firstOrigin) continue;
    const parsed = parseConcordanceEntry(firstOrigin);
    const parentLotNorm = normalizeLotForSort(parsed.lot ?? "");
    if (!parentLotNorm) continue;
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      if (normalizeLotForSort(rows[j].lotDetail?.lot ?? "") === parentLotNorm) {
        childrenOf[j].push(i);
        break;
      }
    }
  }

  const inDegree = rows.map(() => 0);
  for (let i = 0; i < n; i++) {
    for (const c of childrenOf[i]) inDegree[c]++;
  }
  const queue: number[] = [];
  for (let i = 0; i < n; i++) {
    if (inDegree[i] === 0) queue.push(i);
  }
  const result: number[] = [];
  while (queue.length > 0) {
    const i = queue.shift()!;
    result.push(i);
    for (const c of childrenOf[i]) {
      inDegree[c]--;
      if (inDegree[c] === 0) queue.push(c);
    }
  }

  if (result.length !== n) return rows;
  return result.map((i) => rows[i]);
}
